import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { Store } from '../../../../types/Store';
import { OptimisticStoreProvider, useOptimisticStore } from '../../../../context/OptimisticStoreContext';
import { getStoreDisplayLabel } from '../../../../utils/storeHelpers';
import { RIGHTS, useRights, visible } from '../../../../rights';
import { 
    StoreInfo, 
    StoreCoordinates, 
    AdditionalInfo, 
    ContactMag, 
    Precos, 
    Parcours, 
    Urls, 
} from '../../ShowStore/components';

interface StoreLayoutProps {
    store: Store;
    isEditable?: boolean;
    onRefetch?: () => void;
    listStoresOptions?: any[];
}

// Composant interne qui utilise le contexte optimiste
const StoreLayoutContent = ({ store, isEditable = false, onRefetch, listStoresOptions = [] }: StoreLayoutProps) => {
    const { displayStore, setActualStore } = useOptimisticStore();
    // Sections contacts et URL (BeOne : _showModifStore.php:64, 112)
    const { can } = useRights();
    const canSeeContacts = can(visible(RIGHTS.store.contacts));
    const canSeeUrls = can(visible(RIGHTS.store.url));
    
    // Initialiser le store avec les données réelles
    useEffect(() => {
        setActualStore(store);
    }, [store, setActualStore]);
    
    const currentStore = displayStore || store;
    
    const transformedStoresOptions = listStoresOptions.map((store: any) => ({
        data: {
            ENTITY_ID: store.ENTITY_ID,
            IDENTIFIER: store.IDENTIFIER,
            NAME: store.NAME,
            LOCAL_CITY_NAME: store.LOCAL_CITY_NAME,
            ENTITY_TYPE: store.ENTITY_TYPE
        },
        value: store.ENTITY_ID,
        label: `${store.IDENTIFIER || ''} - ${getStoreDisplayLabel(store)}`,
        entity_type: store.ENTITY_TYPE
    }));

    return (
        <Box>
            {/* Section principale: Info et Coordonnées / Infos complémentaires */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 2fr' }, gap: 3, mb: 3, alignItems: 'start' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <StoreInfo store={currentStore} isEditable={isEditable} />
                    <StoreCoordinates store={currentStore} isEditable={isEditable} />
                </Box>
                <AdditionalInfo store={currentStore} isEditable={isEditable} listStoresOptions={transformedStoresOptions} />
            </Box>

            {/* Section Contacts et Precos */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mb: 3, alignItems: 'start' }}>
                {canSeeContacts && <ContactMag store={currentStore} isEditable={isEditable} />}
                <Precos store={currentStore} isEditable={isEditable} />
            </Box>

            {/* Section Parcours et URLs */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.4fr 1fr' }, gap: 3, mb: 3, alignItems: 'start' }}>
                <Parcours store={currentStore} isEditable={isEditable} />
                {canSeeUrls && <Urls store={currentStore} isEditable={isEditable} />}
            </Box>
        </Box>
    );
};

// Composant wrapper avec le provider
const StoreLayout = (props: StoreLayoutProps) => {
    return (
        <OptimisticStoreProvider>
            <StoreLayoutContent {...props} />
        </OptimisticStoreProvider>
    );
};

export default StoreLayout;
